import ExternalLoader, {GeometryRawData, Material, ObjectData} from "_src/engine/loaders/external/external_loader";
import fakeBlob from "_src/engine/fake_blob";
import {unzipSync} from "fflate";
import {isDataZipped} from "_src/utils";
import downloader from "_src/engine/loaders/downloader";
import {messageType} from "_src/engine/ipc";
import workerManager from "_src/engine/worker_manager";

export default class UsdLoader extends ExternalLoader {
  async _parse(data, progress = null) {
    const typedData = new Uint8Array(data);
    const isZip = isDataZipped(typedData);

    const [textureFiles, masterFile] = isZip ? _parseAssets(unzipSync(typedData)) : [[], typedData];
    if (!masterFile) throw new Error( "ExternalLoader.UsdLoader: No master file");

    await _initParser();
    const objectsRawData = (await _parseData(masterFile)).data.objects;
    const objectsData = _postProcess(objectsRawData);
    _fixTexturesPaths(objectsData, isZip, textureFiles, this._basePath);

    if (progress) progress.emit(progress.LOADING, 1);
    return objectsData;
  }
}

function _fixTexturesPaths(objectsData, isZip, texturesMap, basePath) {
  for (const objectData of objectsData) {
    if (!objectData.material.baseTexture) continue;

    if (isZip) {
      objectData.material.baseTexture = texturesMap.get(objectData.material.baseTexture);
    } else {
      objectData.material.baseTexture = basePath + objectData.material.baseTexture;
    }
  }
}

function _postProcess(objectsRawData) {
  const objectsData = [];

  for (const objectRawData of objectsRawData) {
    if (!objectRawData.points && objectRawData.points.length) continue;
    
    const geometryData = new GeometryRawData(objectRawData.points);
    if (objectRawData.normals && objectRawData.normals.length) geometryData.normals = objectRawData.normals;
    if (objectRawData.uvs && objectRawData.uvs.length) geometryData.uvs = objectRawData.uvs;
    
    const materialData = new Material();
    if (objectRawData.texture) materialData.baseTexture = objectRawData.texture;
    
    const objectData = new ObjectData(geometryData, materialData);
    objectsData.push(objectData);
  }

  return objectsData;
}

function _parseAssets( zip ) {

  const textures = new Map();
  let masterFile = null;

  for ( const filename in zip ) {

    if ( filename.endsWith( "png" ) || filename.endsWith("jpg") || filename.endsWith("jpeg") ) {
      const imageType = filename.endsWith( "png" ) ? "image/png" : "image/jpg";

      if ( typeof Blob !== "undefined" ) {
        const blob = new Blob( [ zip[ filename ] ], { type: { type: imageType } } );
        textures.set(filename, URL.createObjectURL(blob));
      } else {
        textures.set(filename, URL.createObjectURL(fakeBlob.registerData(zip[ filename ])));
      }

    }

    if ( filename.endsWith( "usd" ) ||  filename.endsWith( "usda" ) || filename.endsWith( "usdc" )) {
      masterFile = zip[filename];
    }

  }

  return [textures, masterFile];
}

async function _initParser() {
  const srcPrefix = "assets/usdConfig/";
  const dstPrefix = "/";
  const files = [
    "usd/plugInfo.json",
    "usd/usd/resources/plugInfo.json",
    "usd/usdGeom/resources/plugInfo.json",
    "usd/usdShade/resources/plugInfo.json",
  ];
  const configs = new Map();
  const transfer = [];

  for (const file of files) {
    const srcFullPath = srcPrefix + file;
    const data = await downloader.download(srcFullPath, {}, downloader.BINARY);
    const dataTyped = new Uint8Array(data);
    configs.set(dstPrefix + file, dataTyped);
    transfer.push(dataTyped.buffer);
  }

  const message = {
    type : messageType.INIT_USD,
    configs: configs,
  };

  const tid = workerManager.generateUniqueID();
  await workerManager.sendMessage(message, transfer, tid);
}

async function _parseData(data) {
  const message = {
    type : messageType.PARSE_USD,
    data: data,
  };

  const transfer = [data.buffer];
  const tid = workerManager.generateUniqueID();
  const objectsData = await workerManager.sendMessage(message, transfer, tid);

  return objectsData;
}
